import { PermissionCode } from '../../../../auth/permissionCatalog';
import {
  CatalogGap,
  EnvSnapshot,
  GroupCreate,
  GroupMemberPlan,
  GroupMemberSnapshot,
  GroupPlan,
  GroupTargetOnly,
  GroupUpdate,
  RoleCreate,
  RolePlan,
  RoleTargetOnly,
  RoleUpdate,
  ScopeUniverse,
  SkippedGroupMember,
  SyncPlan,
  UserGroupAssign,
  UserGroupPlan,
  UserGroupUnassign,
  UserInvite,
  UserPlan,
  UserReassign,
  UserTargetOnly,
  UserUnmatched,
} from './types';

const emailKey = (email: string): string => email.trim().toLowerCase();
const nameKey = (name: string): string => name.trim().toLowerCase();

function diffSets<T>(source: Set<T>, target: Set<T>): { added: T[]; removed: T[] } {
  const added: T[] = [];
  const removed: T[] = [];
  source.forEach((x) => {
    if (!target.has(x)) added.push(x);
  });
  target.forEach((x) => {
    if (!source.has(x)) removed.push(x);
  });
  return { added, removed };
}

/* ------------------------------------------------------------------ */
/*  Section 1 — Permission catalog (compare-only, never written).      */
/* ------------------------------------------------------------------ */

export function buildCatalogGaps(source: EnvSnapshot, target: EnvSnapshot): CatalogGap[] {
  const rolesByMissingCode = new Map<PermissionCode, Set<string>>();
  for (const role of source.roles) {
    if (role.is_system) continue;
    for (const code of role.permissions) {
      if (!target.permissionCatalog.has(code)) {
        if (!rolesByMissingCode.has(code)) rolesByMissingCode.set(code, new Set());
        rolesByMissingCode.get(code)!.add(role.role_code);
      }
    }
  }
  return Array.from(rolesByMissingCode.entries())
    .map(([missingCode, roles]) => ({ missingCode, affectedRoles: Array.from(roles).sort() }))
    .sort((a, b) => a.missingCode.localeCompare(b.missingCode));
}

const missingPermissionsFor = (permissions: PermissionCode[], gaps: CatalogGap[]): PermissionCode[] => {
  const gapCodes = new Set(gaps.map((g) => g.missingCode));
  return permissions.filter((p) => gapCodes.has(p));
};

/* ------------------------------------------------------------------ */
/*  Section 2 — Roles (matched by role_code, the natural key).         */
/* ------------------------------------------------------------------ */

export function diffRoles(source: EnvSnapshot, target: EnvSnapshot, catalogGaps: CatalogGap[]): RolePlan {
  const targetByCode = new Map(target.roles.filter((r) => !r.is_system).map((r) => [r.role_code, r]));
  const sourceCodes = new Set<string>();
  const create: RoleCreate[] = [];
  const update: RoleUpdate[] = [];

  for (const s of source.roles) {
    if (s.is_system) continue;
    sourceCodes.add(s.role_code);
    const missingPermissions = missingPermissionsFor(s.permissions, catalogGaps);
    const blocked = missingPermissions.length > 0;
    const t = targetByCode.get(s.role_code);

    if (!t) {
      create.push({
        role_code: s.role_code,
        role_name: s.role_name,
        description: s.description,
        permissions: s.permissions,
        blocked,
        missingPermissions,
      });
      continue;
    }

    const nameChanged = t.role_name !== s.role_name;
    const descriptionChanged = (t.description ?? '') !== (s.description ?? '');
    const permDiff = diffSets(new Set(s.permissions), new Set(t.permissions));
    if (nameChanged || descriptionChanged || permDiff.added.length > 0 || permDiff.removed.length > 0) {
      update.push({
        role_code: s.role_code,
        role_name: s.role_name,
        description: s.description,
        permissions: s.permissions,
        blocked,
        missingPermissions,
        nameChanged,
        descriptionChanged,
        permissionsAdded: permDiff.added,
        permissionsRemoved: permDiff.removed,
      });
    }
  }

  const targetOnly: RoleTargetOnly[] = target.roles
    .filter((r) => !r.is_system && !sourceCodes.has(r.role_code))
    .map((r) => ({ role_code: r.role_code, role_name: r.role_name }));

  return { create, update, targetOnly };
}

/* ------------------------------------------------------------------ */
/*  Section 3 — Users (matched by lower-cased email).                  */
/* ------------------------------------------------------------------ */

export function diffUsers(source: EnvSnapshot, target: EnvSnapshot): UserPlan {
  const targetByEmail = new Map(target.users.map((u) => [emailKey(u.email), u]));
  const sourceEmails = new Set<string>();
  const invite: UserInvite[] = [];
  const reassign: UserReassign[] = [];
  const unmatched: UserUnmatched[] = [];

  for (const s of source.users) {
    const key = emailKey(s.email);
    sourceEmails.add(key);

    // Deactivated source holders are never invited or reassigned — that
    // would grant fresh target access on behalf of an account the source
    // already turned off.
    if (!s.active) {
      unmatched.push({ email: key, reason: 'inactive_in_source' });
      continue;
    }

    const t = targetByEmail.get(key);
    if (!t) {
      invite.push({ email: key, display_name: s.display_name, role_v2_code: s.role_v2_code });
      continue;
    }
    if (t.role_v2_code !== s.role_v2_code) {
      reassign.push({
        email: key,
        target_user_id: t.id,
        from_role_code: t.role_v2_code,
        to_role_code: s.role_v2_code,
        display_name: t.display_name,
      });
    }
  }

  const targetOnly: UserTargetOnly[] = target.users
    .filter((u) => !sourceEmails.has(emailKey(u.email)))
    .map((u) => ({ target_user_id: u.id, email: u.email, role_v2_code: u.role_v2_code }));

  return { invite, reassign, unmatched, targetOnly };
}

/* ------------------------------------------------------------------ */
/*  Section 4 — Tenant groups (matched by NAME) + user↔group assign.   */
/* ------------------------------------------------------------------ */

const memberKey = (m: GroupMemberSnapshot | GroupMemberPlan): string => `${m.scope_type}:${m.scope_code}`;

function partitionValidMembers(
  groupName: string,
  members: GroupMemberSnapshot[],
  scopeUniverse: ScopeUniverse,
): { valid: GroupMemberPlan[]; skipped: SkippedGroupMember[] } {
  const valid: GroupMemberPlan[] = [];
  const skipped: SkippedGroupMember[] = [];
  for (const m of members) {
    const universe = scopeUniverse[m.scope_type];
    if (universe?.has(m.scope_code)) {
      valid.push(m);
    } else {
      skipped.push({ group_name: groupName, scope_type: m.scope_type, scope_code: m.scope_code });
    }
  }
  return { valid, skipped };
}

export function diffGroups(source: EnvSnapshot, target: EnvSnapshot): GroupPlan {
  const targetByName = new Map(target.groups.map((g) => [nameKey(g.name), g]));
  const sourceNames = new Set<string>();
  const create: GroupCreate[] = [];
  const update: GroupUpdate[] = [];
  const skippedMembers: SkippedGroupMember[] = [];

  for (const s of source.groups) {
    const key = nameKey(s.name);
    sourceNames.add(key);
    const { valid, skipped } = partitionValidMembers(s.name, s.members, target.scopeUniverse);
    skippedMembers.push(...skipped);
    const t = targetByName.get(key);

    if (!t) {
      create.push({ name: s.name, level: s.level, members: valid, skipped });
      continue;
    }

    const levelChanged = t.level !== s.level;
    const validByKey = new Map(valid.map((m) => [memberKey(m), m]));
    const targetByMemberKey = new Map(t.members.map((m) => [memberKey(m), m]));
    const memberDiff = diffSets(new Set(validByKey.keys()), new Set(targetByMemberKey.keys()));

    if (levelChanged || memberDiff.added.length > 0 || memberDiff.removed.length > 0) {
      update.push({
        target_id: t.id,
        name: s.name,
        level: s.level,
        members: valid,
        skipped,
        levelChanged,
        membersAdded: memberDiff.added.map((k) => validByKey.get(k)!),
        membersRemoved: memberDiff.removed.map((k) => targetByMemberKey.get(k)!),
      });
    }
  }

  const targetOnly: GroupTargetOnly[] = target.groups
    .filter((g) => !sourceNames.has(nameKey(g.name)))
    .map((g) => ({ id: g.id, name: g.name }));

  return { create, update, skippedMembers, targetOnly };
}

/** User↔group assignment sync — only for users PRESENT IN SOURCE (matched
 * existing users AND invite candidates; `userPlan.unmatched` — inactive
 * source holders — are never touched, matching the user-section rule).
 * Target-only users' memberships are left completely alone. */
export function diffUserGroups(source: EnvSnapshot, target: EnvSnapshot, userPlan: UserPlan): UserGroupPlan {
  const targetUserByEmail = new Map(target.users.map((u) => [emailKey(u.email), u]));
  const targetGroupByName = new Map(target.groups.map((g) => [nameKey(g.name), g]));
  const unmatchedEmails = new Set(userPlan.unmatched.map((u) => emailKey(u.email)));

  const assign: UserGroupAssign[] = [];
  const unassign: UserGroupUnassign[] = [];

  for (const s of source.users) {
    const key = emailKey(s.email);
    if (unmatchedEmails.has(key)) continue;

    const t = targetUserByEmail.get(key);
    const sourceGroupNames = new Set(s.groups.map(nameKey));
    const targetGroupNames = new Set((t?.groups ?? []).map(nameKey));
    const diff = diffSets(sourceGroupNames, targetGroupNames);

    for (const gName of diff.added) {
      const tg = targetGroupByName.get(gName);
      assign.push({
        email: key,
        target_user_id: t?.id ?? null,
        group_name: gName,
        target_group_id: tg?.id ?? null,
      });
    }
    // A target user can only have an EXTRA group membership if it already
    // exists on the target — so `t` is always defined here.
    for (const gName of diff.removed) {
      const tg = targetGroupByName.get(gName);
      if (!t || !tg) continue;
      unassign.push({ email: key, target_user_id: t.id, group_name: gName, target_group_id: tg.id });
    }
  }

  return { assign, unassign };
}

/* ------------------------------------------------------------------ */
/*  Entry point.                                                       */
/* ------------------------------------------------------------------ */

export function buildSyncPlan(source: EnvSnapshot, target: EnvSnapshot): SyncPlan {
  const catalogGaps = buildCatalogGaps(source, target);
  const roles = diffRoles(source, target, catalogGaps);
  const users = diffUsers(source, target);
  const groups = diffGroups(source, target);
  const userGroups = diffUserGroups(source, target, users);
  return { targetEnv: target.env, catalogGaps, roles, users, groups, userGroups };
}
